import type { LogProvider } from '../../types/log-provider';

/**
 * ConsoleLogProvider — writes structured JSON logs to stdout/stderr.
 *
 * Environment: universal (safe in both server and client contexts).
 * Registered in: observability/logging/log.server.ts and log.client.ts
 *
 * To replace this provider: implement LogProvider in a new file and swap
 * the import in log.server.ts / log.client.ts. No other files need to change.
 */
export class ConsoleLogProvider implements LogProvider {
  private readonly pretty: boolean;

  constructor({ pretty = false }: { pretty?: boolean } = {}) {
    this.pretty = pretty;
  }

  private format(
    level: string,
    message: string,
    extra?: Record<string, unknown>
  ): string {
    const entry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      ...(extra && { data: extra }),
    };
    return JSON.stringify(entry, null, this.pretty ? 2 : 0);
  }

  info(message: string, data?: Record<string, unknown>): void {
    // biome-ignore lint/suspicious/noConsole: eslint baseline parity
    console.info(this.format('info', message, data));
  }

  error(message: string, error?: Error, extra?: Record<string, unknown>): void {
    const errorFields = error
      ? { error: error.message, stack: error.stack }
      : undefined;
    const data =
      errorFields || extra ? { ...extra, ...errorFields } : undefined;
    // biome-ignore lint/suspicious/noConsole: eslint baseline parity
    console.error(this.format('error', message, data));
  }
}
